import { useRef, useCallback, useEffect, useState } from "react";

export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 250,
  deps: any[] = []
) {
  const timeoutHandler = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    return () => {
      if (timeoutHandler.current) {
        clearTimeout(timeoutHandler.current);
        timeoutHandler.current = null;
      }
    };
  }, []);
  
  const debouncedFn = useCallback(
    (...args: any[]) => {
      if (timeoutHandler.current) clearTimeout(timeoutHandler.current);

      timeoutHandler.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay, ...deps]
  );
  return debouncedFn;
}

/** 
 * Works similar to useMemo except that the recalculation will be debounced by the delay parameter.
 * This is useful for mitigating the performance effects of
 * expensive recalculations that are based on quickly-changing variables.
 */
export function useDebouncedMemo<T>(
  factory: () => T,
  delay: number = 250,
  deps: any[] = []
) {
  const [value, setValue] = useState<T>(factory());
  const timeoutHandler = useRef<NodeJS.Timeout | null>(null);
  const debouncedFn = useCallback(
    () => {
      if (timeoutHandler.current) clearTimeout(timeoutHandler.current);

      timeoutHandler.current = setTimeout(() => {
        setValue(factory());
      }, delay);
    },
    [factory, delay, ...deps]
  );
  useEffect(()=>{
    debouncedFn()
    return () => {
      if (timeoutHandler.current) clearTimeout(timeoutHandler.current);
    };
  }, [debouncedFn])
  return value;
}

/**
 * This is a utility function that emits an event after a certain time window, but only if the dependencies have changed.
 * 
 * This is useful for auto-save behavior, where we want to save the conversation to the db after a certain time window, but only if the conversation has changed.
 * 
 * @param timeWindow - the time window to wait before emitting the event, after a change is observed.
 * @param maxWait - the maximum time to wait before emitting the event, as changes accumulate.
 * @param dependencies - the dependencies to watch for changes
 * @returns the last event time
 */
export function useDebouncedChangeIndicator({ timeWindow, maxWait, dependencies }: { timeWindow: number, maxWait: number, dependencies: any[] }) {
  const [lastEventTime, setLastEventTime] = useState(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      if(Date.now() - lastEventTime > maxWait) {
        setLastEventTime(Date.now());
        // if the last event time is beyond the max wait, 
        // we don't need to set a new timeout
        return;
      }
    }
    
    timeoutRef.current = setTimeout(() => {
      setLastEventTime(Date.now());
    }, timeWindow);
    
  }, dependencies);

  return lastEventTime;
}

/**
 * This is a utility function that emits an event after a certain time window, but only if the dependencies have changed.
 * 
 * This is useful for auto-save behavior, where we want to save the conversation to the db after a certain time window, but only if the conversation has changed.
 * 
 * @param callback - the callback to call when the event is emitted. The callback is called with the last event time.
 * @param opts.timeWindow - the time window to wait before emitting the event, after a change is observed.
 * @param opts.maxWait - the maximum time to wait before emitting the event, as changes accumulate.
 * @param opts.dependencies - the dependencies to watch for changes
 */
export function useDebouncedEffect(callback: (eventTime: number) => void, opts: { timeWindow: number, maxWait: number, dependencies: any[] }) {
  const lastEventTime = useDebouncedChangeIndicator(opts);
  useEffect(() => callback(lastEventTime), [lastEventTime]);
}
